import React, { useState } from "react"
import PropTypes from "prop-types"
import { Modal } from "reactstrap"
import { ErrorMessage, Field, Form, Formik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import { post } from "helpers/api_helper"
import "./PasswordChangeModal.scss"

const AUTH_KEYS = [
  "token",
  "refreshToken",
  "userId",
  "userName",
  "userRole",
  "brokerId",
  "userModule",
  "permissions",
  "isChangePasswordRequired",
]

const SPECIAL = /[@$!%*?&#^()\-_=+{}[\]|\\:;'"<>,./]/

const schema = Yup.object().shape({
  oldPassword: Yup.string().required("Current password is required"),
  password: Yup.string()
    .required("Password is required")
    .min(12, "Password must be at least 12 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()\-_=+{}[\]|\\:;'"<>,./]).{8,}$/,
      "Must contain uppercase, lowercase, number and special character"
    ),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
})

const CHECKS = [
  { key: "length", label: "At least 12 characters", test: value => value.length >= 12 },
  { key: "upper", label: "An uppercase letter (A–Z)", test: value => /[A-Z]/.test(value) },
  { key: "lower", label: "A lowercase letter (a–z)", test: value => /[a-z]/.test(value) },
  { key: "number", label: "A number (0–9)", test: value => /\d/.test(value) },
  { key: "special", label: "A special character (!@#$…)", test: value => SPECIAL.test(value) },
]

const readUserName = () => {
  const stored = localStorage.getItem("userName") || ""
  if (stored) return stored
  try {
    return JSON.parse(localStorage.getItem("authUser"))?.resultData?.userName || ""
  } catch (error) {
    return ""
  }
}

const readBearerToken = () => {
  const stored = localStorage.getItem("token")
  if (stored && stored !== "undefined" && stored !== "null") return stored
  try {
    return JSON.parse(sessionStorage.getItem("authUser"))?.resultData?.bearerToken || ""
  } catch (error) {
    return ""
  }
}

const PasswordField = ({ name, label, autoComplete, invalid, describedBy }) => {
  const [visible, setVisible] = useState(false)
  return (
    <div className="rs-db-pw-field">
      <label htmlFor={`rs-db-${name}`}>{label}</label>
      <div className="rs-db-pw-input">
        <Field
          id={`rs-db-${name}`}
          name={name}
          type={visible ? "text" : "password"}
          autoComplete={autoComplete}
          aria-describedby={describedBy}
          className={`form-control${invalid ? " is-invalid" : ""}`}
        />
        <button
          type="button"
          className="rs-db-pw-eye"
          onClick={() => setVisible(value => !value)}
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        >
          <i className={visible ? "bx bx-hide" : "bx bx-show"} aria-hidden="true" />
        </button>
      </div>
      <ErrorMessage name={name} component="div" className="rs-db-pw-error" />
    </div>
  )
}

PasswordField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  autoComplete: PropTypes.string,
  invalid: PropTypes.bool,
  describedBy: PropTypes.string,
}

const PasswordChangeModal = ({ isOpen }) => {
  const submit = (values, { setSubmitting, resetForm }) => {
    const toastId = toast.loading("Please wait...")
    const token = readBearerToken()
    post(
      "user/changepasswordfirstlogin",
      {
        userId: localStorage.getItem("userId"),
        userName: readUserName(),
        oldPassword: values.oldPassword,
        password: values.password,
      },
      token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    )
      .then(response => {
        toast.dismiss(toastId)
        if (response?.success) {
          toast.success("Password changed successfully. Please sign in with your new password.")
          resetForm()
          AUTH_KEYS.forEach(key => localStorage.removeItem(key))
          sessionStorage.removeItem("authUser")
          window.location.href = "/login"
        } else {
          toast.error(response?.validationErrors?.[0] || "Failed to change password")
        }
      })
      .catch(error => {
        toast.dismiss(toastId)
        if (error?.response?.status === 401) {
          toast.error("Your sign-in session has expired. Please sign in again with your temporary password.")
          return
        }
        toast.error(`Error: ${error?.response?.data?.message || error?.message || "Something went wrong"}`)
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Modal
      isOpen={isOpen}
      backdrop="static"
      keyboard={false}
      centered
      className="rs-db-pw"
      backdropClassName="rs-db-pw-backdrop"
      labelledBy="rs-db-pw-title"
    >
      <div className="rs-db-pw-head">
        <h2 id="rs-db-pw-title">Change your password</h2>
        <span>You need to set a new password before you continue.</span>
      </div>
      <Formik initialValues={{ oldPassword: "", password: "", confirmPassword: "" }} validationSchema={schema} onSubmit={submit}>
        {({ errors, touched, values, isSubmitting }) => (
          <Form className="rs-db-pw-body" noValidate>
            <PasswordField
              name="oldPassword"
              label="Current password"
              autoComplete="current-password"
              invalid={!!(errors.oldPassword && touched.oldPassword)}
            />
            <PasswordField
              name="password"
              label="New password"
              autoComplete="new-password"
              describedBy="rs-db-pw-rules"
              invalid={!!(errors.password && touched.password)}
            />
            <ul className="rs-db-pw-rules" id="rs-db-pw-rules">
              {CHECKS.map(check => {
                const ok = check.test(values.password || "")
                return (
                  <li key={check.key} className={ok ? "is-ok" : ""}>
                    <i className={ok ? "bx bx-check-circle" : "bx bx-circle"} aria-hidden="true" />
                    {check.label}
                  </li>
                )
              })}
            </ul>
            <PasswordField
              name="confirmPassword"
              label="Confirm new password"
              autoComplete="new-password"
              invalid={!!(errors.confirmPassword && touched.confirmPassword)}
            />
            <div className="rs-db-pw-foot">
              <button type="submit" className="rs-db-pw-submit" disabled={isSubmitting}>
                {isSubmitting ? "Updating…" : "Update password"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

PasswordChangeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
}

export default PasswordChangeModal
